/**
 * The words of the theme design panel (fonts, publishing, versions, presets,
 * lock, preview of real pages). Kept apart from dict.ts to keep that file
 * readable; spread into the same two trees, so `dict.test.ts` checks them.
 */
import type { DictNode } from './dict.js'

export const themeDesignIt: DictNode = {
  themeDesign: {
    title: 'Bozza e pubblicazione',
    intro: 'Quello che modifichi resta in bozza finché non pubblichi il tema: il sito pubblico legge solo la versione pubblicata.',
    status: {
      followingDraft: 'Il sito segue la bozza',
      published: 'Pubblicato',
      changed: 'Modifiche non pubblicate',
    },
    followingDraft: 'Questo tema non è mai stato pubblicato, quindi il sito segue ogni modifica salvata. Pubblica il tema per separare la bozza da quello che vedono i lettori.',
    changedBanner: 'Il sito mostra ancora la versione {{version}} del {{date}}. Le modifiche salvate dopo non sono online.',
    publishedBanner: 'La bozza coincide con la versione {{version}}, pubblicata il {{date}}.',
    saveFirst: 'Salva o annulla le modifiche ai token e al CSS prima di pubblicare, bloccare o ripristinare.',
    note: 'Nota della versione',
    notePlaceholder: 'Per esempio: nuovo colore dei pulsanti',
    noteHelp: 'Compare nell’elenco delle versioni e aiuta a ritrovarla.',
    publish: 'Pubblica il tema',
    publishFirst: 'Pubblica la prima versione',
    discard: 'Annulla le modifiche',
    discardTitle: 'Annullare le modifiche non pubblicate?',
    discardMessage: 'La bozza torna alla versione {{version}}, quella che il sito mostra adesso. Le modifiche fatte dopo si perdono, ma la cronologia le conserva.',
    cannotPublish: 'Per pubblicare un tema serve il permesso di pubblicazione.',
    locked: 'Tema bloccato',
    lockedBy: 'Motivo: {{reason}}. Bloccato il {{date}}. Token, varianti, CSS e font sono in sola lettura; la cornice delle pagine si può ancora comporre.',
    lock: 'Blocca il tema',
    unlock: 'Sblocca',
    lockReason: 'Motivo del blocco',
    lockHelp: 'Per esempio: regole di marchio del cliente. Chi proverà a modificare il tema lo leggerà.',
    copyName: 'Nome della copia',
    copyHelp: 'Duplica token, varianti, CSS, font e cornici in un tema nuovo, non bloccato.',
    duplicate: 'Duplica il tema',
    reason: {
      publish: 'Pubblicazione del tema «{{name}}»',
      discard: 'Annullate le modifiche non pubblicate del tema «{{name}}»',
      lock: 'Blocco del tema «{{name}}»',
      unlock: 'Sblocco del tema «{{name}}»',
      duplicate: 'Copia del tema «{{name}}» con il nome «{{copy}}»',
      font: 'Font del tema «{{name}}»',
      fontRole: 'Font di corpo o titoli del tema «{{name}}»',
      fontRemove: 'Rimosso un font dal tema «{{name}}»',
      upload: 'Caricato il font {{name}} per un tema',
      restore: 'Ripristinata la versione {{version}} come nuova bozza',
    },
    done: {
      published: 'Tema pubblicato',
      discarded: 'Modifiche annullate',
      locked: 'Tema bloccato',
      unlocked: 'Tema sbloccato',
      duplicated: 'Tema duplicato',
      restored: 'Versione ripristinata come bozza: pubblicala per metterla online',
    },
    fonts: {
      title: 'Font',
      intro: 'I file stanno nella libreria media del sito: niente servizi esterni. Ogni font si carica con font-display: swap, quelli segnati per il precaricamento partono insieme alla pagina.',
      empty: 'Questo tema usa i caratteri di sistema. Aggiungi un font per cambiarli.',
      system: 'font del dispositivo',
      preload: 'Precaricato',
      readOnly: 'Per aggiungere o cambiare i font serve il permesso di modifica del design, e il tema non deve essere bloccato.',
      inUse: 'È il font di corpo o titoli: scegline un altro prima di rimuoverlo.',
      roles: { body: 'Corpo', heading: 'Titoli' },
      setRole: { body: 'Usa per il corpo', heading: 'Usa per i titoli' },
      clearRole: { body: 'Non più per il corpo', heading: 'Non più per i titoli' },
      styles: { normal: 'Normale', italic: 'Corsivo' },
      add: 'Aggiungi un font',
      addAction: 'Aggiungi il font',
      update: 'Sostituisci il font',
      added: 'Font aggiunto',
      updated: 'Font aggiornato',
      removed: 'Font rimosso',
      roleChanged: 'Font assegnato',
      source: 'Origine',
      sourceAsset: 'File della libreria media (woff2 o woff)',
      sourceSystem: 'Font già presente sul dispositivo',
      file: 'File del font',
      fileHelp: 'Solo woff2 (consigliato) o woff, al massimo 1 MB: un sottoinsieme latino pesa 20-60 KB.',
      chooseFile: 'Scegli un file…',
      upload: 'Carica un file font',
      uploading: 'Caricamento…',
      family: 'Famiglia',
      familyHelp: 'Il nome che usa il CSS, per esempio Inter.',
      weight: 'Peso',
      style: 'Stile',
      fallback: 'Alternative',
      fallbackHelp: 'Cosa mostra il browser mentre il file arriva o se non arriva.',
      preloadLabel: 'Precarica questo font',
      preloadHelp: 'Solo per quello del testo in alto: al massimo tre font per tema.',
      willReplace: 'Esiste già un font con questa famiglia, peso e stile ({{id}}): verrà sostituito.',
      problems: {
        family: 'Scrivi il nome della famiglia.',
        file: 'Scegli o carica un file woff2 o woff.',
        systemPreload: 'Un font del dispositivo non ha un file da precaricare.',
      },
    },
    versions: {
      title: 'Versioni e confronto',
      intro: 'Ogni pubblicazione è una versione. Confronta due versioni, o una con la bozza, e ripristina una vecchia versione come nuova bozza.',
      empty: 'Nessuna versione: appare alla prima pubblicazione del tema.',
      name: 'Versione {{version}}',
      liveChip: 'Online',
      draft: 'Bozza',
      live: 'Online adesso (versione {{version}})',
      numbered: 'Versione {{version}} · {{date}}',
      summary: '{{tokens}} token, {{fonts}} font, CSS {{css}}',
      compareWithDraft: 'Confronta con la bozza',
      restore: 'Ripristina come bozza',
      from: 'Da',
      to: 'A',
      pickTwo: 'Scegli due versioni diverse per vedere cosa cambia.',
      same: 'Le due versioni sono identiche.',
      totals: 'Differenze: {{tokens}} token, {{variants}} varianti, {{fonts}} font, {{css}} righe di CSS.',
      tokens: 'Token',
      variants: 'Varianti di colore',
      fonts: 'Font',
      css: 'CSS aggiuntivo ({{added}} righe aggiunte, {{removed}} tolte)',
      truncated: 'Mostrate solo le prime righe: il resto è omesso.',
    },
    preview: {
      title: 'Anteprima su pagine reali',
      intro: 'Vedi la bozza del tema su una pagina o un contenuto del sito, resi dallo stesso motore che pubblica.',
      choose: 'Pagina da vedere',
      pages: 'Pagine composte',
      contents: 'Contenuti',
      empty: 'Il sito non ha ancora pagine o contenuti pubblicati da mostrare.',
      draftNote: 'Mostra la bozza, anche se non è ancora online. La pagina non è indicizzabile.',
      frame: 'Anteprima della pagina con la bozza del tema',
      open: 'Apri a tutto schermo',
    },
    presets: {
      title: 'Temi di partenza',
      intro: 'Preset sobri da cui iniziare. Il sito ottiene una copia tutta sua: cambiare il preset più avanti non cambia i siti già creati.',
      use: 'Crea un tema',
      reason: 'Nuovo tema dal preset «{{name}}»',
    },
  },
}

export const themeDesignEn: DictNode = {
  themeDesign: {
    title: 'Draft and publishing',
    intro: 'What you change stays a draft until you publish the theme: the public site reads only the published version.',
    status: {
      followingDraft: 'The site follows the draft',
      published: 'Published',
      changed: 'Unpublished changes',
    },
    followingDraft: 'This theme was never published, so the site follows every saved change. Publish the theme to separate the draft from what readers see.',
    changedBanner: 'The site still shows version {{version}} from {{date}}. Changes saved since are not online.',
    publishedBanner: 'The draft matches version {{version}}, published on {{date}}.',
    saveFirst: 'Save or discard the token and CSS edits before publishing, locking or restoring.',
    note: 'Version note',
    notePlaceholder: 'For example: new button color',
    noteHelp: 'Shown in the version list to help find it later.',
    publish: 'Publish the theme',
    publishFirst: 'Publish the first version',
    discard: 'Discard changes',
    discardTitle: 'Discard the unpublished changes?',
    discardMessage: 'The draft goes back to version {{version}}, the one the site shows now. Later changes are lost, but the history keeps them.',
    cannotPublish: 'Publishing a theme needs the publish permission.',
    locked: 'Theme locked',
    lockedBy: 'Reason: {{reason}}. Locked on {{date}}. Tokens, variants, CSS and fonts are read-only; page frames can still be composed.',
    lock: 'Lock the theme',
    unlock: 'Unlock',
    lockReason: 'Reason for the lock',
    lockHelp: 'For example: the client’s brand rules. Anyone trying to edit the theme will read it.',
    copyName: 'Name of the copy',
    copyHelp: 'Copies tokens, variants, CSS, fonts and frames into a new, unlocked theme.',
    duplicate: 'Duplicate the theme',
    reason: {
      publish: 'Published theme “{{name}}”',
      discard: 'Discarded the unpublished changes of theme “{{name}}”',
      lock: 'Locked theme “{{name}}”',
      unlock: 'Unlocked theme “{{name}}”',
      duplicate: 'Copy of theme “{{name}}” named “{{copy}}”',
      font: 'Font of theme “{{name}}”',
      fontRole: 'Body or heading font of theme “{{name}}”',
      fontRemove: 'Removed a font from theme “{{name}}”',
      upload: 'Uploaded the font {{name}} for a theme',
      restore: 'Restored version {{version}} as the new draft',
    },
    done: {
      published: 'Theme published',
      discarded: 'Changes discarded',
      locked: 'Theme locked',
      unlocked: 'Theme unlocked',
      duplicated: 'Theme duplicated',
      restored: 'Version restored as a draft: publish it to put it online',
    },
    fonts: {
      title: 'Fonts',
      intro: 'Files live in the site’s media library: no external services. Every font loads with font-display: swap, and those marked for preload start with the page.',
      empty: 'This theme uses system fonts. Add a font to change them.',
      system: 'device font',
      preload: 'Preloaded',
      readOnly: 'Adding or changing fonts needs the design write permission, and the theme must not be locked.',
      inUse: 'It is the body or heading font: choose another one before removing it.',
      roles: { body: 'Body', heading: 'Headings' },
      setRole: { body: 'Use for body', heading: 'Use for headings' },
      clearRole: { body: 'Not for body any more', heading: 'Not for headings any more' },
      styles: { normal: 'Normal', italic: 'Italic' },
      add: 'Add a font',
      addAction: 'Add the font',
      update: 'Replace the font',
      added: 'Font added',
      updated: 'Font updated',
      removed: 'Font removed',
      roleChanged: 'Font assigned',
      source: 'Source',
      sourceAsset: 'Media library file (woff2 or woff)',
      sourceSystem: 'Font already on the device',
      file: 'Font file',
      fileHelp: 'woff2 (recommended) or woff only, up to 1 MB: a Latin subset is 20-60 KB.',
      chooseFile: 'Choose a file…',
      upload: 'Upload a font file',
      uploading: 'Uploading…',
      family: 'Family',
      familyHelp: 'The name the CSS uses, for example Inter.',
      weight: 'Weight',
      style: 'Style',
      fallback: 'Fallbacks',
      fallbackHelp: 'What the browser shows while the file loads or if it never does.',
      preloadLabel: 'Preload this font',
      preloadHelp: 'Only for the text at the top: at most three fonts per theme.',
      willReplace: 'A font with this family, weight and style already exists ({{id}}): it will be replaced.',
      problems: {
        family: 'Write the family name.',
        file: 'Choose or upload a woff2 or woff file.',
        systemPreload: 'A device font has no file to preload.',
      },
    },
    versions: {
      title: 'Versions and comparison',
      intro: 'Every publication is a version. Compare two versions, or one with the draft, and restore an old version as the new draft.',
      empty: 'No versions yet: one appears when the theme is first published.',
      name: 'Version {{version}}',
      liveChip: 'Online',
      draft: 'Draft',
      live: 'Online now (version {{version}})',
      numbered: 'Version {{version}} · {{date}}',
      summary: '{{tokens}} tokens, {{fonts}} fonts, CSS {{css}}',
      compareWithDraft: 'Compare with the draft',
      restore: 'Restore as draft',
      from: 'From',
      to: 'To',
      pickTwo: 'Pick two different versions to see what changes.',
      same: 'The two versions are identical.',
      totals: 'Differences: {{tokens}} tokens, {{variants}} variants, {{fonts}} fonts, {{css}} CSS lines.',
      tokens: 'Tokens',
      variants: 'Color variants',
      fonts: 'Fonts',
      css: 'Additional CSS ({{added}} lines added, {{removed}} removed)',
      truncated: 'Only the first lines are shown: the rest is omitted.',
    },
    preview: {
      title: 'Preview on real pages',
      intro: 'See the theme draft on a page or content of the site, rendered by the same engine that publishes.',
      choose: 'Page to view',
      pages: 'Composed pages',
      contents: 'Contents',
      empty: 'The site has no published pages or contents to show yet.',
      draftNote: 'Shows the draft, even if it is not online yet. The page is not indexable.',
      frame: 'Page preview with the theme draft',
      open: 'Open full screen',
    },
    presets: {
      title: 'Starting themes',
      intro: 'Sober presets to start from. The site gets a copy of its own: changing the preset later does not change sites already created.',
      use: 'Create a theme',
      reason: 'New theme from preset “{{name}}”',
    },
  },
}
